/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-case-declarations */
import { useContext } from 'react';

import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { Text } from '@intigral/shared';
import { Carousel2, CastAndCrewCard } from '@intigral/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import h, { getCountry } from '../../helpers/common';
import f from '../../helpers/formatter';
import f2 from '../../helpers/formatter2';
import url from '../../helpers/url';
import useToggleDetailOverlay from '../../hooks/useToggleDetailOverlay';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { EVENT } from '../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { handleBiography } from '../../managers/dataLoaderHandler';
import {
    close as closeCastCrewModal,
    open as openCastCrewModal,
    updateContent as updateCastCrewModalContent,
} from '../../store/reducers/castCrewModalSlice';
import { __ } from '../../translations';
import { transformFilmography } from '../../utils/transformResponse/mpxResponses';
import CastCrewModal from './CastCrewModal';

export const ImagesCarousel = ({ carousel, meta }) => {
    //  This section is only for Div vod carousel ,
    //  if Guid is not present for promotions and need to show Div contents,then getting guid from all assets/series apis and pass it div apis.
    const {
        data: content,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['biography', carousel?.id],
        queryFn: async () => handleBiography(carousel),
        enabled: !!carousel,
        staleTime: 30 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        retry: 2,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const dispatch = useDispatch();
    const castCrewModal = useSelector((state: any) => state.castCrewModal);
    const isModalOpened = castCrewModal?.isOpen;
    const modalContent = castCrewModal?.modalContent;

    // Using React Query instead of Redux for configuration
    const { paidVsFree } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable;
    // const [searchParams, setSearchParams] = useSearchParams();
    const { showDetailOverlay, removeSearchParams } = useToggleDetailOverlay();

    const settings = {
        pagination: {
            el: '.paging',
            clickable: true,
        },
        breakpoints: {
            560: {
                slidesPerView: 4,
            },
            768: {
                slidesPerView: 6,
            },
            1212: {
                slidesPerView: 8,
            },
            1500: {
                slidesPerView: 9,
            },
        },
    };

    const componentTitle = f.getComponentTitle(meta);
    const formattedTitle = f2.getPlaceholderValue(componentTitle);
    const heading = (
        <Text
            as="h2"
            variant="h2"
            alignment="start"
            margin={{ default: '2x', md: '3x' }}
            size={{ default: '18', md: '28' }}
        >
            {formattedTitle}
        </Text>
    );
    const loadingCard = <CastAndCrewCard DetailsButton={(children) => children} photoSrc="" title="" isLoading />;
    const { track }: { track: any } = useContext(MixpanelContext);

    const openDetailOverlay = (item) => {
        dispatch(closeCastCrewModal());
        removeSearchParams(['movie', 'series', 'guid', 'season']);
        const itemId = item.Id;
        const typeKey = item.programType === 'movie' ? 'movie' : 'series';
        showDetailOverlay({
            [typeKey]: btoa(itemId),
            guid: btoa(item?.guid),
            season: btoa(item?.tvSeasonNumber || item?.seasonNumber || null),
        });
    };

    const filmographyMutation = useMutation({
        mutationFn: async (biography: any) => {
            const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
            const filmographyResponse = await mpxService.getFilmography(`{PC}{${getCountry()}}`, biography.id);
            const filmography = transformFilmography(filmographyResponse.entries);
            return { filmography, biography };
        },
        onSuccess: (data) => {
            const position = Array.isArray(data.biography.position)
                ? _.uniqBy(data.biography.position as any[], 'creditType').map((pos: any) => {
                      return pos.creditType;
                  })
                : data.biography.position;
            dispatch(
                updateCastCrewModalContent({
                    ...modalContent,
                    filmography: data.filmography,
                    position,
                    title: data.biography.title,
                    image: data.biography.image,
                })
            );
            track(EVENT.CAST_CREW.FILMOGRAPHY_VIEW, {
                CastName: data?.biography?.title,
                CastRole: Array.isArray(position) ? position?.join(',') : position,
            });
        },
    });

    const modalSettings = {
        isModalOpened,
        closeModal() {
            dispatch(closeCastCrewModal());
        },
        async openModal(modalContentArg) {
            filmographyMutation.mutate(modalContentArg);
            dispatch(openCastCrewModal({}));
        },
        modalContent,
    };

    if (isLoading && atob(url.getQuery('guid')) !== 'null') {
        return (
            <div className="container full-width">
                {heading}
                <Carousel2 swiperSettings={settings} isLoading>
                    {loadingCard}
                </Carousel2>
            </div>
        );
    }

    if (error) {
        return null;
    }

    return (
        content &&
        content.length > 0 && (
            <div className="container full-width" id={carousel?.id} data-test-id={carousel?.id}>
                {heading}
                <Carousel2 swiperSettings={settings} isLoading={isLoading}>
                    {isLoading
                        ? loadingCard
                        : content.map((item, index) => {
                              const position = _.uniqBy(item.position, 'creditType')
                                  .map((pos) => {
                                      return __(`cast_crew_${(pos.creditType?.trim() || '').toLowerCase()}`);
                                  })
                                  .join(', ');
                              return (
                                  <CastAndCrewCard
                                      DetailsButton={(children) => <span>{children}</span>}
                                      photoSrc={h.getImagizerUrl(item?.image, 350)}
                                      title={item?.title}
                                      position={position || null}
                                      key={`CastAndCrew_${index}`}
                                      onClick={() => modalSettings.openModal(item)}
                                  />
                              );
                          })}
                </Carousel2>

                <CastCrewModal
                    isModalOpened={isModalOpened}
                    modalContent={modalContent}
                    closeModal={modalSettings.closeModal}
                    openDetailOverlay={openDetailOverlay}
                    enableLockIcon={enableLockIcon}
                />
            </div>
        )
    );
};
